import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaFacebookF, FaTwitter, FaLinkedinIn, FaYoutube, FaInstagram, FaPinterestP, FaYelp } from 'react-icons/fa';
import { Link } from 'gatsby';
import { Row, Col, Form, Container } from 'react-bootstrap';
import { IoIosClose } from 'react-icons/io';
import { WMKLink as WMKLink$1 } from 'wmk-lib';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import { Player, ControlBar } from 'video-react';
import Img from 'gatsby-image';
import Helmet from 'react-helmet';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

var wmkClass = function wmkClass(name, group, append, prefix) {
  if (append === void 0) {
    append = "";
  }

  if (prefix === void 0) {
    prefix = "wmk";
  }

  var classes = [prefix + "-" + group, prefix + "-" + group + "-" + name, append];
  return classes.join(" ").trim();
};

var Anchor = React.forwardRef(function (_ref, ref) {
  var to = _ref.to,
      id = _ref.id,
      className = _ref.className,
      target = _ref.target,
      children = _ref.children,
      style = _ref.style,
      speed = _ref.speed,
      mailto = _ref.mailto,
      tel = _ref.tel,
      animate = _ref.animate,
      rel = _ref.rel,
      label = _ref.label;

  var _style = animate ? _extends(_extends({}, style), {}, {
    transition: "all " + speed + "s ease"
  }) : style;

  var _target = target ? "_" + target.replace("_", "") : null;

  var _rel = _target === "_blank" ? "noopener noreferrer" : rel;

  var prefix = tel ? "tel:" : mailto ? "mailto:" : "";

  var _to = tel ? to.replace(/\D/g, "") : to;

  var _link = tel ? "tel" : mailto ? "mailto" : "anchor";

  return /*#__PURE__*/React.createElement("a", {
    href: prefix + _to,
    id: id ? id : undefined,
    className: wmkClass(_link, "link", className),
    target: _target,
    rel: _rel,
    ref: ref,
    style: _style,
    "aria-label": label
  }, children);
});
Anchor.defaultProps = {
  target: false,
  id: false,
  className: "",
  speed: ".3",
  mailto: false,
  tel: false,
  animate: false
};
var SocialMediaIcon = React.forwardRef(function (_ref, ref) {
  var platform = _ref.platform,
      url = _ref.url,
      target = _ref.target,
      className = _ref.className;
  var Icon = platform.Icon,
      name = platform.name;

  var _className = name + (className !== "" ? " " + className : "");

  return /*#__PURE__*/React.createElement(Anchor, {
    key: name,
    to: url,
    target: target,
    className: _className,
    ref: ref
  }, /*#__PURE__*/React.createElement(Icon, null), /*#__PURE__*/React.createElement("span", {
    className: "sr-only sr-only-focusable"
  }, "Open " + name + " page"));
});
SocialMediaIcon.defaultProps = {
  target: "_blank",
  className: ""
};

var SocialMediaIcons = function SocialMediaIcons(_ref) {
  var query = _ref.query,
      platforms = _ref.platforms,
      className = _ref.className;

  var getPlatform = function getPlatform(platform, platforms) {
    var socialPlatform = null;
    platforms.forEach(function (p) {
      if (platform.toLowerCase().indexOf(p.name) !== -1) {
        socialPlatform = p;
      }
    });
    return socialPlatform;
  };

  var _className = wmkClass("icon", "social", className);

  return /*#__PURE__*/React.createElement(React.Fragment, null, query.map(function (icon) {
    var title = icon.title,
        url = icon.url,
        target = icon.target;
    var platform = getPlatform(title, platforms);
    return /*#__PURE__*/React.createElement(SocialMediaIcon, {
      platform: platform,
      url: url,
      target: target,
      className: _className,
      key: platform.name + "-icon"
    });
  }));
};

SocialMediaIcons.defaultProps = {
  platforms: [{
    name: "facebook",
    Icon: FaFacebookF
  }, {
    name: "twitter",
    Icon: FaTwitter
  }, {
    name: "linkedin",
    Icon: FaLinkedinIn
  }, {
    name: "youtube",
    Icon: FaYoutube
  }, {
    name: "instagram",
    Icon: FaInstagram
  }, {
    name: "pinterest",
    Icon: FaPinterestP
  }, {
    name: "yelp",
    Icon: FaYelp
  }],
  className: ""
};
var WMKLink = React.forwardRef(function (_ref, ref) {
  var to = _ref.to,
      id = _ref.id,
      children = _ref.children,
      target = _ref.target,
      mailto = _ref.mailto,
      tel = _ref.tel,
      style = _ref.style,
      className = _ref.className,
      speed = _ref.speed,
      animate = _ref.animate,
      label = _ref.label;

  var _className = wmkClass("gatsby", "link", className);

  var _target = target ? "_" + target.replace("_", "") : null;

  var _to = mailto || tel ? to !== "/" ? to : children : to;

  return _target && _target !== "_internal" || _target && _target !== "_link" || mailto || tel ? /*#__PURE__*/React.createElement(Anchor, {
    to: _to,
    mailto: mailto,
    tel: tel,
    target: target,
    className: className,
    id: id,
    ref: ref,
    style: style,
    speed: speed,
    animate: animate,
    label: label
  }, children) : /*#__PURE__*/React.createElement(Link, {
    to: to,
    ref: ref,
    className: _className,
    id: id ? id : undefined,
    style: style,
    "aria-label": label
  }, children);
});
WMKLink.defaultProps = {
  tel: false,
  mailto: false,
  id: false,
  target: false,
  className: "",
  to: "/",
  animate: false
};

var SearchSliderDrawer = function SearchSliderDrawer(_ref) {
  var isSearchOpen = _ref.isSearchOpen,
      setIsSearchOpen = _ref.setIsSearchOpen,
      query = _ref.query,
      Result = _ref.Result,
      Close = _ref.Close,
      className = _ref.className,
      style = _ref.style,
      useAlgorithm = _ref.useAlgorithm;

  var _useState = useState(null),
      searchResults = _useState[0],
      setSearchResults = _useState[1];

  var _useState2 = useState(""),
      searchKey = _useState2[0],
      setSearchKey = _useState2[1];

  var _style = _extends(_extends({}, style), {}, {
    position: isSearchOpen ? "fixed" : "absolute",
    zIndex: !isSearchOpen ? -1 : 20000,
    width: isSearchOpen ? "100%" : "1px",
    minHeight: isSearchOpen ? "100%" : "none",
    height: isSearchOpen ? "auto" : "1px",
    transition: "all 0.3s ease",
    right: 0,
    top: 0,
    overflow: isSearchOpen ? "scroll" : "hidden"
  });

  var getSearchIndex = function getSearchIndex(query) {
    var searchIndices = Object.keys(query).map(function (key) {
      var data = query[key].edges.map(function (edge) {
        edge.node["nodeType"] = key;
        return edge.node;
      });
      var all = data.map(function (datum) {
        return datum;
      });
      return all;
    });
    var searchIndex = [].concat.apply([], searchIndices).sort(function (a, b) {
      return a.title > b.title ? 1 : -1;
    });
    return searchIndex;
  };

  var searchIndex = getSearchIndex(query);

  var handleSearch = function handleSearch(e) {
    var _useAlgorithm = useAlgorithm(e, searchIndex),
        key = _useAlgorithm.key,
        results = _useAlgorithm.results;

    setSearchKey(key);
    setSearchResults(results);
  };

  var handleClose = function handleClose() {
    setIsSearchOpen(false);
  };

  var handleSumbit = function handleSumbit(e) {
    e.preventDefault();
  };

  var _className = [];

  _className.push(isSearchOpen ? "search-open" : "search-closed");

  _className.push(className);

  return /*#__PURE__*/React.createElement("div", {
    style: _style,
    className: wmkClass("drawer", "search", _className.join(" "))
  }, /*#__PURE__*/React.createElement(Row, {
    className: "flex-column",
    style: {
      width: "100%"
    }
  }, /*#__PURE__*/React.createElement(Col, {
    style: {
      width: "100%",
      display: "flex",
      justifyContent: "flex-end",
      padding: "2vh 3vw",
      minHeight: "5rem"
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: handleClose,
    "aria-label": "Close Search",
    className: "close"
  }, /*#__PURE__*/React.createElement(Close, null))), /*#__PURE__*/React.createElement(Col, {
    style: {
      display: "flex",
      justifyContent: "center",
      minHeight: "5rem"
    }
  }, /*#__PURE__*/React.createElement(Form, {
    style: {
      width: "80%"
    },
    onSubmit: handleSumbit,
    className: "search-form"
  }, /*#__PURE__*/React.createElement(Form.Control, {
    placeholder: "Enter Search...",
    onChange: handleSearch
  }))), /*#__PURE__*/React.createElement(Col, {
    className: "results"
  }, /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(Row, {
    style: {
      maxWidth: "80%",
      margin: "auto"
    },
    className: "flex-column"
  }, searchResults && searchResults.length && searchKey !== "" ? searchResults.map(function (result, i) {
    return /*#__PURE__*/React.createElement(Col, {
      key: result.title + i
    }, /*#__PURE__*/React.createElement(Result, {
      result: result
    }));
  }) : searchResults && !searchResults.length && searchKey !== "" ? /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement("em", null, "No results matching ", /*#__PURE__*/React.createElement("strong", null, searchKey), "."), /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("sub", null, "Please try another search."))) : null)))));
};

SearchSliderDrawer.defaultProps = {
  Result: function Result(_ref2) {
    var result = _ref2.result;
    return /*#__PURE__*/React.createElement(WMKLink$1, {
      to: result.slug
    }, result.title);
  },
  Close: function Close() {
    return /*#__PURE__*/React.createElement(IoIosClose, null);
  },
  useAlgorithm: function useAlgorithm(event, index) {
    var search = index.filter(function (item) {
      var lower = item.title.toLowerCase();
      return lower.includes(event.target.value.toLowerCase());
    });
    return {
      key: event.target.value,
      results: search
    };
  }
};

var SearchSliderOpen = function SearchSliderOpen(_ref) {
  var isSearchOpen = _ref.isSearchOpen,
      setIsSearchOpen = _ref.setIsSearchOpen,
      className = _ref.className,
      children = _ref.children,
      style = _ref.style;

  var handleFocus = function handleFocus() {
    setIsSearchOpen(true);
  };

  var _className = [];

  _className.push(isSearchOpen ? "search-open" : "search-closed");

  _className.push(className);

  return /*#__PURE__*/React.createElement("button", {
    className: wmkClass("open", "search", _className.join(" ")),
    onClick: handleFocus,
    style: style ? style : undefined
  }, children);
};

SearchSliderOpen.defaultProps = {
  className: ""
};
var Search = {
  Slider: {
    Drawer: SearchSliderDrawer,
    Button: SearchSliderOpen
  }
};
var SearchSliderOpen$1 = SearchSliderOpen;
var SearchSliderDrawer$1 = SearchSliderDrawer;
var Copyright = React.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      className = _ref.className,
      id = _ref.id;
  var year = new Date().getFullYear();
  return /*#__PURE__*/React.createElement("div", {
    id: id,
    className: wmkClass("copyright", "layout", className),
    ref: ref
  }, "\xA9 ", year, " ", children);
});

var FlexSpacer = function FlexSpacer(_ref) {
  var className = _ref.className,
      id = _ref.id;
  return /*#__PURE__*/React.createElement("div", {
    className: wmkClass("flex-spacer", "layout", className),
    id: id,
    style: {
      margin: "auto"
    }
  });
};

FlexSpacer.defaultProps = {
  id: "",
  className: ""
};
var Footer = React.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      className = _ref.className,
      id = _ref.id;
  return /*#__PURE__*/React.createElement("footer", {
    className: wmkClass("footer", "layout", className),
    id: id,
    ref: ref
  }, children);
});

var MainLayout = function MainLayout(_ref) {
  var children = _ref.children,
      Header = _ref.Header,
      Footer = _ref.Footer,
      className = _ref.className;
  var HeaderJSX = Header;
  var FooterJSX = Footer;
  return /*#__PURE__*/React.createElement("div", {
    className: wmkClass("wrap", "layout"),
    style: {
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column"
    }
  }, /*#__PURE__*/React.createElement(HeaderJSX, null), /*#__PURE__*/React.createElement("main", {
    className: wmkClass("main", "layout", className)
  }, children), /*#__PURE__*/React.createElement(FlexSpacer, null), /*#__PURE__*/React.createElement(FooterJSX, null));
};

MainLayout.defaultProps = {
  Header: function Header() {
    return /*#__PURE__*/React.createElement("div", null, "Pass Header JSX");
  },
  Footer: function Footer() {
    return /*#__PURE__*/React.createElement("div", null, "Pass Footer JSX");
  }
};
var Header = React.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      className = _ref.className,
      style = _ref.style;
  return /*#__PURE__*/React.createElement("header", {
    className: wmkClass("header", "layout", className),
    style: style,
    ref: ref
  }, children);
});

var Sticky = function Sticky(_ref) {
  var Alert = _ref.Alert,
      className = _ref.className,
      children = _ref.children,
      absolute = _ref.absolute,
      style = _ref.style,
      zIndex = _ref.zIndex,
      width = _ref.width,
      trigger = _ref.trigger;
  var domPosition = absolute ? "absolute" : "relative";

  var _useState = useState(0),
      headerHeight = _useState[0],
      setHeaderHeight = _useState[1];

  var _useState2 = useState(0),
      scrollPos = _useState2[0],
      setScrollPos = _useState2[1];

  var headerRef = useRef();
  useEffect(function () {
    var handleResize = function handleResize() {
      setHeaderHeight(headerRef.current.getBoundingClientRect().height);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return function () {
      return window.removeEventListener("resize", handleResize);
    };
  }, [headerHeight]);
  useScrollPosition(function (_ref2) {
    var currPos = _ref2.currPos;
    setScrollPos(currPos.y * -1);
  });
  var classes = [className];
  var triggerHeight = trigger >= 0 ? trigger : headerHeight;
  var isTriggered = scrollPos > triggerHeight;
  if (isTriggered) classes.push("stuck");
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: wmkClass("sticky-header", "layout", classes.join(" ")),
    style: _extends(_extends({}, style), {}, {
      position: isTriggered ? "fixed" : domPosition,
      zIndex: zIndex,
      width: width
    })
  }, Alert, /*#__PURE__*/React.createElement(Header, {
    ref: headerRef
  }, children)), isTriggered && /*#__PURE__*/React.createElement("div", {
    style: {
      height: headerHeight
    }
  }));
};

Sticky.defaultProps = {
  Alert: /*#__PURE__*/React.createElement(React.Fragment, null),
  className: "",
  absolute: false,
  zIndex: 1000,
  width: "100%",
  trigger: 0
};

var ListItemColumn = function ListItemColumn(_ref) {
  var list = _ref.list,
      JSX = _ref.JSX;
  return /*#__PURE__*/React.createElement(Row, {
    className: "flex-column"
  }, list && list.length ? list.map(function (li, i) {
    return /*#__PURE__*/React.createElement(JSX, {
      key: li + i,
      li: li
    });
  }) : null);
};

ListItemColumn.defaultProps = {
  JSX: function JSX(_ref2) {
    var li = _ref2.li;
    return /*#__PURE__*/React.createElement(Col, null, console.log(li), " List Item Data");
  }
};

var ListItemColumns = function ListItemColumns(_ref) {
  var list = _ref.list,
      cols = _ref.cols,
      JSX = _ref.JSX,
      className = _ref.className;
  var sortList = [].concat(list);
  var total = sortList.length;
  var per = Math.floor(total / cols);
  var rem = total % cols;
  var columns = [];

  for (var i = 0; i < cols; i++) {
    var retVal = i < cols - 1 ? per : per + rem;
    var column = [];

    for (var j = 0; j < retVal; j++) {
      column.push(sortList.shift());
    }

    columns.push(column);
  }

  return columns && columns.length ? /*#__PURE__*/React.createElement(Row, {
    className: wmkClass('list-item-columns', 'layout', className)
  }, columns.map(function (col, i) {
    var randomString = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    return /*#__PURE__*/React.createElement(Col, {
      key: col + "-" + randomString + "-" + i,
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4
    }, /*#__PURE__*/React.createElement(ListItemColumn, {
      list: col,
      JSX: JSX
    }));
  })) : null;
};

ListItemColumns.defaultProps = {
  cols: 3,
  JSX: function JSX(_ref2) {
    var li = _ref2.li;
    return /*#__PURE__*/React.createElement(Col, null, console.log(li), " List Item Data");
  },
  className: ""
};

var Spinner = function Spinner() {
  return /*#__PURE__*/React.createElement("div", {
    style: {
      width: "100%",
      textAlign: "center",
      position: "absolute",
      top: 0,
      left: 0,
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 5
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "lds-ring"
  }, /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement("div", null)));
};

var Video = function Video(_ref) {
  var id = _ref.id,
      className = _ref.className,
      url = _ref.url,
      poster = _ref.poster,
      dimensions = _ref.dimensions,
      Loader = _ref.Loader;

  var _useState = useState(null),
      readyState = _useState[0],
      setReadyState = _useState[1];

  var _useState2 = useState(false),
      hasLoaded = _useState2[0],
      setHasLoaded = _useState2[1];

  var _useState3 = useState(0),
      winWidth = _useState3[0],
      setWinWidth = _useState3[1];

  var playerRef = useRef();
  useEffect(function () {
    playerRef.current.subscribeToStateChange(function (state, prevState) {
      setReadyState(state.readyState);
      if (state.readyState > 3) setHasLoaded(true);
    });

    var handleResize = function handleResize() {
      setWinWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return function () {
      return window.removeEventListener("resize", handleResize);
    };
  });
  var edgeLong = dimensions.split("x")[0];
  var edgeShort = dimensions.split("x")[1];

  var _id = id ? id : undefined;

  return /*#__PURE__*/React.createElement("div", {
    id: _id,
    className: wmkClass("video", "media", className),
    style: {
      position: "relative"
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: "https://via.placeholder.com/" + (winWidth > edgeShort ? edgeLong + "x" + edgeShort : edgeShort + "x" + edgeShort) + ".jpg",
    alt: "",
    style: {
      width: "100%",
      maxWidth: "none",
      visibility: "hidden"
    }
  }), (!readyState || readyState < 4) && !hasLoaded ? /*#__PURE__*/React.createElement(Loader, null) : null, /*#__PURE__*/React.createElement(Player, {
    ref: playerRef,
    poster: poster,
    preload: "auto",
    muted: true,
    autoPlay: true,
    loop: true,
    playsInline: true
  }, /*#__PURE__*/React.createElement("source", {
    src: url
  }), /*#__PURE__*/React.createElement(ControlBar, {
    disableCompletely: true
  })));
};

Video.defaultProps = {
  id: false,
  url: "https://test-videos.co.uk/vids/bigbuckbunny/mp4/h264/1080/Big_Buck_Bunny_1080_10s_5MB.mp4",
  poster: "",
  dimensions: "1280x720",
  Loader: Spinner
};

var LightBoxImage = function LightBoxImage(_ref) {
  var url = _ref.url,
      alt = _ref.alt,
      caption = _ref.caption,
      fluid = _ref.fluid,
      prefix = _ref.prefix;

  var _useState = useState(false),
      isExpanded = _useState[0],
      setIsExpanded = _useState[1];

  var handleExpand = function handleExpand() {
    setIsExpanded(!isExpanded);
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("figure", {
    className: prefix + "-wrap"
  }, /*#__PURE__*/React.createElement("button", {
    "aria-label": "Expand " + alt + " image",
    style: {
      border: "none",
      appearance: "none"
    },
    onClick: handleExpand
  }, /*#__PURE__*/React.createElement("img", {
    className: prefix,
    src: url,
    alt: alt
  })), caption && /*#__PURE__*/React.createElement("figcaption", {
    className: prefix + "-caption"
  }, caption)), isExpanded ? /*#__PURE__*/React.createElement(Container, {
    fluid: true,
    style: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(0,0,0,.75)",
      zIndex: 5000
    }
  }, /*#__PURE__*/React.createElement(Row, {
    className: "flex-column",
    style: {
      width: "100%"
    }
  }, /*#__PURE__*/React.createElement(Col, {
    style: {
      display: "flex",
      justifyContent: "flex-end",
      "-webkit-flex": "initial"
    }
  }, /*#__PURE__*/React.createElement("button", {
    "aria-label": "Close lightbox",
    onClick: handleExpand,
    style: {
      margin: "3vh 3vh 0 3vh",
      background: "none",
      color: "white",
      appearance: "none",
      border: "none",
      fontSize: "4rem",
      lineHeight: 0,
      padding: 0
    }
  }, /*#__PURE__*/React.createElement(IoIosClose, null))), /*#__PURE__*/React.createElement(Col, {
    style: {
      display: "flex",
      justifyContent: "center",
      "-webkit-flex": "initial"
    }
  }, /*#__PURE__*/React.createElement("img", {
    className: prefix + "-lightbox",
    src: url,
    alt: alt,
    style: {
      width: fluid ? "100%" : "auto",
      maxWidth: fluid ? "none" : "100%",
      maxHeight: fluid ? "80vh" : "none",
      objectFit: "contain"
    }
  })), caption && /*#__PURE__*/React.createElement(Col, {
    style: {
      display: "flex",
      justifyContent: "center",
      "-webkit-flex": "initial"
    }
  }, /*#__PURE__*/React.createElement("figcaption", {
    className: prefix + "-lightbox-caption",
    style: {
      color: "white",
      padding: ".5rem"
    }
  }, caption)))) : null);
};

LightBoxImage.defaultProps = {
  alt: "",
  fluid: false,
  prefix: "embedded-image"
};

var MenuItems = function MenuItems(_ref) {
  var Header = _ref.Header,
      header = _ref.header,
      menus = _ref.menus,
      Menu = _ref.Menu;
  return /*#__PURE__*/React.createElement(Row, {
    className: "flex-column"
  }, /*#__PURE__*/React.createElement(Header, null, header), menus && menus.map(function (item, i) {
    var title = item.title,
        target = item.target,
        to = item.to;
    return /*#__PURE__*/React.createElement(Menu, {
      key: title + i,
      to: to,
      target: target
    }, title);
  }));
};

MenuItems.defaultProps = {
  Header: function Header(_ref2) {
    var children = _ref2.children;
    return /*#__PURE__*/React.createElement(Col, null, children);
  },
  Menu: function Menu(_ref3) {
    var to = _ref3.to,
        target = _ref3.target,
        children = _ref3.children;
    return /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement(WMKLink, {
      to: to,
      target: target
    }, children));
  },
  menus: []
};

var ContentfulLogo = function ContentfulLogo(_ref) {
  var fluid = _ref.fluid,
      fixed = _ref.fixed,
      contentType = _ref.contentType,
      url = _ref.url,
      id = _ref.id,
      className = _ref.className,
      to = _ref.to,
      alt = _ref.alt,
      target = _ref.target;
  var isFixed = fixed && !fixed.notAProp ? fixed : false;
  var isFluid = fluid && !fluid.notAProp ? fluid : false;
  var JSX = null;

  switch (true) {
    case contentType.indexOf("svg") !== -1:
      JSX = function JSX(_ref2) {
        var src = _ref2.src,
            alt = _ref2.alt,
            className = _ref2.className,
            id = _ref2.id;
        return /*#__PURE__*/React.createElement("img", {
          className: wmkClass("logo", "ctfl", className),
          id: id,
          src: src,
          alt: alt
        });
      };

      break;

    case isFixed !== false:
      JSX = function JSX(_ref3) {
        var fixed = _ref3.fixed,
            alt = _ref3.alt,
            id = _ref3.id,
            className = _ref3.className;
        return /*#__PURE__*/React.createElement(Img, {
          className: wmkClass("logo", "ctfl", className),
          id: id,
          fixed: fixed,
          alt: alt
        });
      };

      break;

    case isFluid !== false:
      JSX = function JSX(_ref4) {
        var fluid = _ref4.fluid,
            alt = _ref4.alt,
            id = _ref4.id,
            className = _ref4.className;
        return /*#__PURE__*/React.createElement(Img, {
          className: wmkClass("logo", "ctfl", className),
          id: id,
          fluid: fluid,
          alt: alt
        });
      };

      break;

    default:
      JSX = function JSX() {
        return /*#__PURE__*/React.createElement("div", null, "Contentful Image Error");
      };

  }

  return target === "internal" ? /*#__PURE__*/React.createElement(WMKLink, {
    to: to
  }, /*#__PURE__*/React.createElement(JSX, {
    src: url,
    alt: alt,
    fixed: isFixed,
    fluid: isFluid,
    className: className,
    id: id
  })) : /*#__PURE__*/React.createElement(WMKLink, {
    to: to,
    target: target
  }, /*#__PURE__*/React.createElement(JSX, {
    src: url,
    alt: alt,
    fixed: isFixed,
    fluid: isFluid,
    className: className,
    id: id
  }));
};

ContentfulLogo.defaultProps = {
  fluid: {
    notAProp: true
  },
  fixed: {
    notAProp: true
  },
  url: "",
  id: "",
  className: "",
  to: "/",
  target: "internal"
};
var SEO = {};

var sanitizeSocialImageUrl = function sanitizeSocialImageUrl(url) {
  return url.indexOf("//") === 0 ? "https:" + url : url;
};

SEO.Meta = function (_ref) {
  var description = _ref.description,
      lang = _ref.lang,
      title = _ref.title,
      path = _ref.path,
      slug = _ref.slug,
      ogImage = _ref.ogImage,
      twitterImage = _ref.twitterImage,
      twitterHandle = _ref.twitterHandle,
      baseUrl = _ref.baseUrl,
      siteTitle = _ref.siteTitle;
  var slugVar = !slug || slug === "/" ? "" : slug;
  var pathVar = !slug || slug === "/" ? "" : path;
  var metaProps = [{
    name: "description",
    content: description
  }, {
    property: "og:title",
    content: title
  }, {
    property: "og:description",
    content: description
  }, {
    property: "og:type",
    content: "website"
  }, {
    property: "og:url",
    content: "" + baseUrl + pathVar + slugVar
  }, {
    property: "og:sitename",
    content: baseUrl
  }, {
    name: "twitter:card",
    content: "summary"
  }, {
    name: "twitter:creator",
    content: siteTitle
  }, {
    name: "twitter:image:alt",
    content: title
  }, {
    name: "twitter:title",
    content: title
  }, {
    name: "twitter:description",
    content: description
  }];

  if (ogImage) {
    metaProps.push({
      property: "og:image",
      content: sanitizeSocialImageUrl(ogImage)
    });
  } else {
    console.log("No Open Graph Image set in SEO.Meta");
  }

  if (twitterImage) {
    metaProps.push({
      name: "twitter:image",
      content: sanitizeSocialImageUrl(twitterImage)
    });
  } else {
    console.log("No Twitter Image set in SEO.Meta");
  }

  if (twitterHandle) {
    metaProps.push({
      name: "twitter:site",
      content: twitterHandle
    });
  } else {
    console.log("No Twitter Handle set in SEO.Meta");
  }

  return /*#__PURE__*/React.createElement(Helmet, {
    htmlAttributes: {
      lang: lang
    },
    link: [{
      rel: "canonical",
      href: "" + baseUrl + pathVar + slugVar
    }],
    title: title,
    titleTemplate: "%s | " + siteTitle,
    meta: metaProps
  });
};

SEO.Meta.defaultProps = {
  lang: "en",
  description: "",
  path: "/"
};
SEO.Meta.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  title: PropTypes.string.isRequired,
  path: PropTypes.string,
  slug: PropTypes.string.isRequired,
  ogImage: PropTypes.string,
  twitterImage: PropTypes.string,
  twitterHandle: PropTypes.string,
  baseUrl: PropTypes.string.isRequired,
  siteTitle: PropTypes.string.isRequired
};

var generateGraph = function generateGraph(type, graphMeta, baseUrl) {
  if (graphMeta === void 0) {
    graphMeta = {};
  }

  var info = {
    "@type": type,
    "@id": baseUrl + "/#" + type.toLowerCase()
  };
  return _extends(_extends({}, info), graphMeta);
};

SEO.Schema = function (_ref2) {
  var graphs = _ref2.graphs,
      baseUrl = _ref2.baseUrl,
      siteTitle = _ref2.siteTitle;
  var website = generateGraph("WebSite", {
    name: siteTitle,
    url: baseUrl,
    publisher: {
      "@id": baseUrl + "/#organization"
    }
  }, baseUrl);
  graphs.push(website);
  var schema = {
    "@context": "https://schema.org",
    "@graph": []
  };
  graphs.forEach(function (graph) {
    return schema["@graph"].push(graph);
  });
  return /*#__PURE__*/React.createElement(Helmet, null, /*#__PURE__*/React.createElement("script", {
    type: "application/ld+json"
  }, JSON.stringify(schema)));
};

SEO.Schema.defaultProps = {
  graphs: []
};
SEO.Schema.propTypes = {
  graphs: PropTypes.array,
  baseUrl: PropTypes.string.isRequired,
  siteTitle: PropTypes.string.isRequired
};
export { Anchor, ContentfulLogo, Copyright, FlexSpacer, Footer, Header, LightBoxImage, ListItemColumns, MainLayout, MenuItems, SEO, Search, SearchSliderDrawer$1 as SearchSliderDrawer, SearchSliderOpen$1 as SearchSliderOpen, SocialMediaIcons, Sticky, Video, WMKLink, generateGraph };